// src/pages/LoyaltyCheckinPage.jsx
// LOOP D1.4 — Customer QR check-in / loyalty lookup
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { MessageCircle, ArrowLeft, RefreshCw, CheckCircle2, Gift, QrCode, CalendarDays, Link2 } from "lucide-react";

function initials(name) {
  const parts = String(name || "Cliente").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return (parts[0] || "CL").slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

function loyaltyProgressValues(account = {}, program = {}) {
  const isPoints = program?.type === "points";
  const required = Math.max(1, Number(isPoints ? (program?.pointsRequired || account?.pointsRequired || 100) : (program?.stampsRequired || account?.stampsRequired || 5)));
  const balance = Math.max(0, Number(isPoints ? (account?.pointsBalance || 0) : (account?.stampsBalance || 0)));
  const unit = isPoints ? "punto" : "sello";
  return { isPoints, required, balance, unit };
}

function statusText(account = {}, program = {}) {
  const { required, balance, unit } = loyaltyProgressValues(account, program);
  const rewards = Math.max(0, Number(account?.rewardsAvailable || 0));
  if (rewards > 0) return `${rewards} recompensa${rewards === 1 ? "" : "s"} disponible${rewards === 1 ? "" : "s"}`;
  const missing = Math.max(0, required - balance);
  return missing > 0 ? `Faltan ${missing} ${unit}${missing === 1 ? "" : "s"}` : "Lista para recompensa";
}

function formatQrDate(value) {
  try {
    if (!value) return "Sin registro";
    const d = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "Sin registro";
    return d.toLocaleString("es-PA", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "Sin registro";
  }
}


function formatBookingCandidateDate(value) {
  try {
    if (!value) return "Sin fecha";
    const d = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "Sin fecha";
    return d.toLocaleString("es-PA", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return "Sin fecha";
  }
}

function bookingCandidateAmount(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n <= 0) return "";
  return `$${n % 1 === 0 ? n.toFixed(0) : n.toFixed(2)}`;
}

const LOOP_APP_SCHEME = "loopreservas://";
const LOOP_SMART_OPEN_STORAGE_PREFIX = "loop_smart_open_loyalty_checkin_";

function isLoopNativeRuntime() {
  try {
    if (typeof window === "undefined") return false;
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === "function") {
      return window.Capacitor.isNativePlatform();
    }
    if (window.Capacitor && typeof window.Capacitor.getPlatform === "function") {
      return window.Capacitor.getPlatform() !== "web";
    }
    const protocol = String(window.location?.protocol || "").toLowerCase();
    return protocol === "capacitor:" || protocol === "ionic:";
  } catch (_) {
    return false;
  }
}

function isMobileLikeBrowser() {
  try {
    if (typeof navigator === "undefined") return false;
    const ua = String(navigator.userAgent || navigator.vendor || "").toLowerCase();
    const touch = typeof navigator.maxTouchPoints === "number" ? navigator.maxTouchPoints > 1 : false;
    return /iphone|ipad|ipod|android|mobile|tablet/.test(ua) || touch;
  } catch (_) {
    return false;
  }
}

function buildLoopAppCheckinUrl(token) {
  const cleanToken = String(token || "").trim();
  return cleanToken ? `${LOOP_APP_SCHEME}loyalty/checkin/${encodeURIComponent(cleanToken)}` : "";
}

function shouldAutoOpenLoopApp() {
  try {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search || "");
    const value = String(params.get("open") || params.get("app") || params.get("smartOpen") || "").toLowerCase();
    return ["app", "loop", "1", "true", "yes"].includes(value);
  } catch (_) {
    return false;
  }
}

function markSmartOpenAttempt(token) {
  try {
    if (typeof window === "undefined" || !window.sessionStorage) return false;
    const key = `${LOOP_SMART_OPEN_STORAGE_PREFIX}${String(token || "").slice(0, 120)}`;
    if (window.sessionStorage.getItem(key) === "1") return false;
    window.sessionStorage.setItem(key, "1");
    return true;
  } catch (_) {
    return true;
  }
}

function openLoopAppForToken(token) {
  const appUrl = buildLoopAppCheckinUrl(token);
  if (!appUrl || typeof window === "undefined") return "";
  window.location.href = appUrl;
  return appUrl;
}


function BookingCandidateSelector({ candidates = [], selectedBookingId = "", onSelect }) {
  return (
    <div className="lcp-booking-link">
      <div className="lcp-booking-link-head">
        <div>
          <span>RESERVA / PAGO</span>
          <h3>Aplicar esta recompensa</h3>
        </div>
        <Link2 size={20} />
      </div>

      <button
        type="button"
        className={`lcp-booking-option lcp-booking-general ${!selectedBookingId ? "is-selected" : ""}`}
        onClick={() => onSelect("")}
      >
        <div>
          <strong>Redimir sin vincular a reserva</strong>
          <small>Solo descuenta la recompensa del cliente.</small>
        </div>
        {!selectedBookingId ? <CheckCircle2 size={18} /> : null}
      </button>

      {candidates.length ? (
        <div className="lcp-booking-options">
          {candidates.map((booking) => {
            const selected = selectedBookingId === booking.id || selectedBookingId === booking.bookingId;
            return (
              <button
                type="button"
                key={booking.id || booking.bookingId}
                className={`lcp-booking-option ${selected ? "is-selected" : ""} ${booking.canApply === false ? "is-disabled" : ""}`}
                disabled={booking.canApply === false}
                onClick={() => onSelect(booking.bookingId || booking.id)}
              >
                <div className="lcp-booking-option-icon"><CalendarDays size={18} /></div>
                <div>
                  <strong>{booking.serviceName || "Reserva"}</strong>
                  <small>{formatBookingCandidateDate(booking.scheduledAt)} · {booking.status || "confirmed"}{bookingCandidateAmount(booking.amount) ? ` · ${bookingCandidateAmount(booking.amount)}` : ""}</small>
                  {booking.blockReason ? <em>{booking.blockReason}</em> : null}
                </div>
                {selected ? <CheckCircle2 size={18} /> : null}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="lcp-booking-empty">No encontramos reservas recientes para este cliente. Puedes redimir la recompensa sin vincularla a una reserva.</p>
      )}
    </div>
  );
}

function ProgramCard({ item, busyKey, onAdjust, onRedeem, readOnly = false }) {
  const program = item?.program || {};
  const account = item?.account || {};
  const { isPoints, required, balance, unit } = loyaltyProgressValues(account, program);
  const rewards = Math.max(0, Number(account.rewardsAvailable || 0));
  const pct = Math.max(0, Math.min(100, Math.round((balance / required) * 100)));
  const key = program.id || account.programId;
  const busy = busyKey === key;

  return (
    <article className={`lcp-program ${rewards > 0 ? "has-reward" : ""}`}>
      <div className="lcp-program-head">
        <div>
          <strong>{program.name || "Programa de sellos"}</strong>
          <span>{program.rewardLabel || "Recompensa"}</span>
        </div>
        <em>{balance}/{required}{isPoints ? " pts" : ""}</em>
      </div>
      <div className="lcp-progress"><i style={{ width: `${pct}%` }} /></div>
      <div className="lcp-program-meta">
        <span>{statusText(account, program)}</span>
        <small>{program.type === "points" ? "Programa de puntos" : (program.eligibleMode === "selected" ? "Servicios seleccionados" : "Todos los servicios")}</small>
      </div>
      {!readOnly ? (
        <div className="lcp-actions-grid">
          <button type="button" disabled={busy} onClick={() => onAdjust(program.id, isPoints ? Math.max(1, Number(program.pointManualStep || 10)) : 1)}>{isPoints ? `+ ${Math.max(1, Number(program.pointManualStep || 10))} pts` : "+ Sello"}</button>
          <button type="button" disabled={busy || balance <= 0} onClick={() => onAdjust(program.id, isPoints ? -Math.max(1, Number(program.pointManualStep || 10)) : -1)}>{isPoints ? `- ${Math.max(1, Number(program.pointManualStep || 10))} pts` : "- Sello"}</button>
          <button type="button" disabled={busy || rewards <= 0} className="primary" onClick={() => onRedeem(program.id)}>Redimir</button>
        </div>
      ) : null}
    </article>
  );
}

export default function LoyaltyCheckinPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [busyKey, setBusyKey] = useState("");
  const [selectedBookingId, setSelectedBookingId] = useState("");
  const [smartOpenAttempted, setSmartOpenAttempted] = useState(false);
  const [smartOpenDismissed, setSmartOpenDismissed] = useState(false);
  const isRedemptionMode = String(payload?.mode || "").toLowerCase() === "redemption";

  const load = async () => {
    const cleanToken = String(token || "").trim();
    if (!cleanToken) {
      setError("QR inválido o incompleto.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const callable = httpsCallable(getFunctions(), "getLoyaltyCheckinByToken");
      const res = await callable({ token: cleanToken });
      setPayload(res?.data || null);
    } catch (e) {
      console.error("[LoyaltyCheckinPage] load failed", e);
      const code = String(e?.code || "");
      if (code.includes("permission-denied")) setError("No tienes permisos para ver este cliente. Abre Loop con la cuenta del negocio.");
      else if (code.includes("not-found")) setError("Este QR no existe o fue desactivado.");
      else setError(String(e?.message || "") || "No pudimos abrir este QR de fidelidad.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const cleanToken = String(token || "").trim();
    if (!cleanToken || isLoopNativeRuntime() || !isMobileLikeBrowser() || !shouldAutoOpenLoopApp()) return undefined;

    setSmartOpenAttempted(true);
    if (!markSmartOpenAttempt(cleanToken)) return undefined;

    const timer = window.setTimeout(() => {
      openLoopAppForToken(cleanToken);
    }, 450);

    return () => window.clearTimeout(timer);
  }, [token]);

  useEffect(() => {
    if (!payload || !isRedemptionMode) return;

    const lockedBookingId = String(payload?.redemption?.bookingId || "").trim();
    if (lockedBookingId) {
      setSelectedBookingId(lockedBookingId);
      return;
    }

    const candidates = Array.isArray(payload?.bookingCandidates) ? payload.bookingCandidates : [];
    const firstApplicable = candidates.find((item) => item?.canApply !== false && (item?.bookingId || item?.id));
    setSelectedBookingId((prev) => prev || String(firstApplicable?.bookingId || firstApplicable?.id || ""));
  }, [payload, isRedemptionMode]);

  const adjust = async (programId, delta) => {
    if (!payload?.businessId || !payload?.customer?.id || !programId) return;
    setBusyKey(programId);
    setError("");
    try {
      const callable = httpsCallable(getFunctions(), "adjustCustomerLoyaltyStamps");
      await callable({
        businessId: payload.businessId,
        customerId: payload.customer.id,
        programId,
        delta,
        reason: delta > 0 ? "qr_checkin_add_stamp" : "qr_checkin_remove_stamp",
      });
      await load();
    } catch (e) {
      console.error("[LoyaltyCheckinPage] adjust failed", e);
      setError("No pudimos ajustar los sellos desde el QR.");
    } finally {
      setBusyKey("");
    }
  };

  const redeem = async (programId) => {
    if (!payload?.businessId || !payload?.customer?.id || !programId) return;
    setBusyKey(programId);
    setError("");
    try {
      const callable = httpsCallable(getFunctions(), "redeemCustomerLoyaltyReward");
      await callable({
        businessId: payload.businessId,
        customerId: payload.customer.id,
        programId,
        reason: "qr_checkin_redeem_reward",
      });
      await load();
    } catch (e) {
      console.error("[LoyaltyCheckinPage] redeem failed", e);
      setError("No pudimos redimir la recompensa desde el QR.");
    } finally {
      setBusyKey("");
    }
  };

  const redeemRedemptionToken = async () => {
    const cleanToken = String(token || "").trim();
    if (!cleanToken) return;
    setBusyKey("redemption");
    setError("");
    try {
      const bookingId = String(selectedBookingId || "").trim();
      const callable = httpsCallable(getFunctions(), "redeemLoyaltyRedemptionToken");
      const res = await callable({
        token: cleanToken,
        ...(bookingId ? { bookingId } : {}),
      });
      const result = res?.data || {};
      setPayload((prev) => ({
        ...(prev || {}),
        redemption: {
          ...(prev?.redemption || {}),
          status: "used",
          usedAt: new Date().toISOString(),
          eventId: result.eventId || prev?.redemption?.eventId || null,
          bookingId: result.bookingId || selectedBookingId || prev?.redemption?.bookingId || null,
        },
        redemptionResult: result,
      }));
    } catch (e) {
      console.error("[LoyaltyCheckinPage] redemption token redeem failed", e);
      const msg = String(e?.message || "");
      setError(msg || "No pudimos redimir este QR temporal.");
    } finally {
      setBusyKey("");
    }
  };

  const customer = payload?.customer || {};
  const qr = payload?.qr || {};
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const bookingCandidates = Array.isArray(payload?.bookingCandidates) ? payload.bookingCandidates : [];
  const redemptionStatus = String(payload?.redemption?.status || "").toLowerCase();
  const redemptionUsed = redemptionStatus === "used";
  const cleanTokenForSmartOpen = String(token || "").trim();
  const canSmartOpen = Boolean(cleanTokenForSmartOpen && !isLoopNativeRuntime() && isMobileLikeBrowser());
  const showSmartOpenCard = canSmartOpen && !smartOpenDismissed;
  const appCheckinUrl = buildLoopAppCheckinUrl(cleanTokenForSmartOpen);

  return (
    <div className="lcp-page">
      <style>{styles}</style>
      <main className="lcp-shell">
        <button type="button" className="lcp-back" onClick={() => navigate(-1)}><ArrowLeft size={16} /> Volver</button>

        <section className="lcp-hero">
          <div>
            <span>QR DE FIDELIDAD</span>
            <h1>Check-in de cliente</h1>
            <p>Escaneo seguro para consultar fidelidad, confirmar canjes y dejar trazabilidad para el negocio.</p>
          </div>
          <button type="button" className="lcp-refresh" onClick={load} disabled={loading}><RefreshCw size={16} /> Actualizar</button>
        </section>

        {showSmartOpenCard ? (
          <div className={`lcp-smart-open ${smartOpenAttempted ? "was-attempted" : ""}`}>
            <div>
              <strong>{smartOpenAttempted ? "Intentando abrir Loop..." : "Abrir en la app de Loop"}</strong>
              <span>Si tienes Loop instalado, este QR puede abrirse directo en la app. Si no abre, continúa aquí en web.</span>
            </div>
            <div className="lcp-smart-open-actions">
              <a href={appCheckinUrl} onClick={() => setSmartOpenAttempted(true)}>Abrir app</a>
              <button type="button" onClick={() => setSmartOpenDismissed(true)}>Seguir en web</button>
            </div>
          </div>
        ) : null}

        {loading ? <div className="lcp-card lcp-center">Cargando QR...</div> : null}
        {error ? <div className="lcp-alert">{error}</div> : null}

        {!loading && payload && isRedemptionMode ? (
          <section className="lcp-grid">
            <aside className="lcp-card lcp-customer-card">
              <div className="lcp-avatar">{initials(customer.name)}</div>
              <h2>{customer.name || "Cliente"}</h2>
              <p>{customer.phone || customer.email || "Sin contacto"}</p>
              <div className="lcp-stats">
                <div><strong>{customer.totalBookings || 0}</strong><span>Citas</span></div>
                <div><strong>{customer.hasUserAccount ? "Sí" : "No"}</strong><span>Usuario</span></div>
                <div><strong>{redemptionUsed ? "Usada" : "Pendiente"}</strong><span>Recompensa</span></div>
              </div>
              <div className="lcp-qr-note">QR temporal generado por el cliente.</div>
            </aside>

            <section className="lcp-card lcp-redemption-card">
              <div className="lcp-section-head">
                <div>
                  <span>REDENCIÓN DE FIDELIDAD</span>
                  <h2>{redemptionUsed ? "Recompensa usada" : "Confirmar recompensa"}</h2>
                </div>
                <Gift size={24} />
              </div>

              <div className={`lcp-redemption-preview ${redemptionUsed ? "is-used" : ""}`}>
                <div className="lcp-redemption-icon"><QrCode size={26} /></div>
                <strong>{payload?.redemption?.rewardLabel || "Recompensa"}</strong>
                <p>
                  {redemptionUsed
                    ? "Esta recompensa ya fue marcada como usada y no puede volver a canjearse."
                    : "Confirma el uso solo cuando estés seguro de aplicarla al cliente. El canje quedará registrado en historial."}
                </p>
              </div>

              {redemptionUsed ? (
                <div className="lcp-result-card">
                  <CheckCircle2 size={20} />
                  <div>
                    <strong>Canje confirmado</strong>
                    <small>{payload?.redemption?.usedAtIso || payload?.redemption?.usedAt ? `Usado: ${formatQrDate(payload.redemption.usedAtIso || payload.redemption.usedAt)}` : "Esta recompensa ya fue consumida."}</small>
                  </div>
                </div>
              ) : null}

              {!redemptionUsed ? (
                <BookingCandidateSelector
                  candidates={bookingCandidates}
                  selectedBookingId={selectedBookingId}
                  onSelect={setSelectedBookingId}
                />
              ) : payload?.redemption?.bookingId ? (
                <div className="lcp-booking-linked-done">
                  <CheckCircle2 size={18} />
                  <span>Recompensa aplicada a la reserva {payload.redemption.bookingId}</span>
                </div>
              ) : null}


              {items.map((item) => (
                <ProgramCard
                  key={item.program?.id || item.account?.programId}
                  item={item}
                  busyKey={busyKey}
                  onAdjust={() => {}}
                  onRedeem={() => {}}
                  readOnly
                />
              ))}

              <button
                type="button"
                className="lcp-confirm-redemption"
                disabled={busyKey === "redemption" || redemptionUsed}
                onClick={redeemRedemptionToken}
              >
                {redemptionUsed ? "Canje confirmado" : busyKey === "redemption" ? "Confirmando..." : "Confirmar uso de recompensa"}
              </button>
            </section>
          </section>
        ) : null}

        {!loading && payload && !isRedemptionMode ? (
          <section className="lcp-grid">
            <aside className="lcp-card lcp-customer-card">
              <div className="lcp-avatar">{initials(customer.name)}</div>
              <h2>{customer.name || "Cliente"}</h2>
              <p>{customer.phone || customer.email || "Sin contacto"}</p>
              <div className="lcp-stats">
                <div><strong>{customer.totalBookings || 0}</strong><span>Citas</span></div>
                <div><strong>{customer.hasUserAccount ? "Sí" : "No"}</strong><span>Usuario</span></div>
                <div><strong>{qr.scansCount || 0}</strong><span>Escaneos previos</span></div>
              </div>
              <div className="lcp-qr-note">Último escaneo previo: {formatQrDate(qr.lastUsedAt)}</div>
              <div className="lcp-links">
                {customer.phone ? <a href={`https://wa.me/${String(customer.phone).replace(/\D/g, "")}`} target="_blank" rel="noreferrer"><MessageCircle size={15} /> WhatsApp</a> : null}
                <button type="button" onClick={() => navigate(`/business/${encodeURIComponent(payload.businessId)}/customers?customerId=${encodeURIComponent(customer.id)}`)}>Ver CRM</button>
              </div>
            </aside>

            <section className="lcp-card">
              <div className="lcp-section-head">
                <div>
                  <span>FIDELIDAD</span>
                  <h2>Sellos y recompensas</h2>
                </div>
                <CheckCircle2 size={24} />
              </div>

              {!items.length ? (
                <div className="lcp-empty">Este negocio todavía no tiene programas activos para este cliente.</div>
              ) : null}

              {items.map((item) => (
                <ProgramCard
                  key={item.program?.id || item.account?.programId}
                  item={item}
                  busyKey={busyKey}
                  onAdjust={adjust}
                  onRedeem={redeem}
                />
              ))}
            </section>
          </section>
        ) : null}
      </main>
    </div>
  );
}

const styles = `
.lcp-page{min-height:100vh;background:radial-gradient(circle at top left,rgba(0,103,95,.12),transparent 32%),linear-gradient(180deg,#f8fafc,#fff);padding:24px 14px 90px;color:#0f172a;box-sizing:border-box}.lcp-shell{width:min(100%,1080px);margin:0 auto;display:grid;gap:14px}.lcp-back,.lcp-refresh{width:fit-content;min-height:40px;border:1px solid #dbe4ef;background:#fff;border-radius:999px;padding:0 14px;font-weight:950;color:#334155;display:inline-flex;align-items:center;gap:8px;cursor:pointer}.lcp-hero{border:1px solid #dbe4ef;background:linear-gradient(135deg,rgba(0,103,95,.08),#fff);border-radius:32px;padding:24px;display:flex;align-items:flex-end;justify-content:space-between;gap:16px;box-shadow:0 18px 48px rgba(15,23,42,.07)}.lcp-hero span,.lcp-section-head span{color:#00675f;font-size:11px;font-weight:950;letter-spacing:.14em;text-transform:uppercase}.lcp-hero h1{margin:7px 0 0;font-size:42px;line-height:.96;letter-spacing:-.06em;font-weight:950}.lcp-hero p{margin:10px 0 0;color:#64748b;font-weight:750;max-width:650px}.lcp-grid{display:grid;grid-template-columns:330px minmax(0,1fr);gap:14px;align-items:start}.lcp-card{border:1px solid #dbe4ef;background:rgba(255,255,255,.96);border-radius:30px;padding:18px;box-shadow:0 18px 48px rgba(15,23,42,.07)}.lcp-smart-open{border:1px solid rgba(0,103,95,.18);background:linear-gradient(135deg,rgba(0,103,95,.08),#fff);border-radius:24px;padding:14px 16px;display:flex;align-items:center;justify-content:space-between;gap:14px;box-shadow:0 14px 34px rgba(15,23,42,.06)}.lcp-smart-open strong{display:block;color:#0f172a;font-size:14px;font-weight:950}.lcp-smart-open span{display:block;margin-top:3px;color:#64748b;font-size:12px;font-weight:800;line-height:1.35}.lcp-smart-open-actions{display:flex;align-items:center;gap:8px;flex:0 0 auto}.lcp-smart-open-actions a,.lcp-smart-open-actions button{min-height:38px;border-radius:999px;padding:0 13px;font-size:12px;font-weight:950;text-decoration:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer}.lcp-smart-open-actions a{background:#00675f;color:#fff;border:1px solid #00675f}.lcp-smart-open-actions button{background:#fff;color:#334155;border:1px solid #dbe4ef}@media(max-width:680px){.lcp-smart-open{display:grid}.lcp-smart-open-actions{display:grid;grid-template-columns:1fr 1fr;width:100%}.lcp-smart-open-actions a,.lcp-smart-open-actions button{width:100%}}
.lcp-center{min-height:180px;display:grid;place-items:center;font-weight:900;color:#64748b}.lcp-alert{border:1px solid #fed7aa;background:#fff7ed;color:#c2410c;border-radius:22px;padding:14px;font-weight:900}.lcp-customer-card{text-align:center;position:sticky;top:14px}.lcp-avatar{width:96px;height:96px;border-radius:32px;background:linear-gradient(135deg,rgba(0,103,95,.10),#f8fafc);display:grid;place-items:center;margin:8px auto 12px;color:#00675f;font-weight:950;font-size:28px}.lcp-customer-card h2{margin:0;font-size:28px;line-height:1;font-weight:950;letter-spacing:-.055em}.lcp-customer-card p{margin:8px 0 0;color:#64748b;font-weight:760}.lcp-stats{display:grid;grid-template-columns:1fr 1fr;gap:9px;margin-top:16px}.lcp-stats div{border:1px solid #e2e8f0;background:#f8fafc;border-radius:20px;padding:13px}.lcp-stats strong{display:block;font-size:22px;font-weight:950}.lcp-stats span{display:block;margin-top:4px;color:#64748b;font-size:11px;font-weight:900}.lcp-links{display:grid;grid-template-columns:1fr;gap:8px;margin-top:14px}.lcp-links a,.lcp-links button{min-height:43px;border:1px solid #dbe4ef;background:#fff;border-radius:15px;color:#334155;text-decoration:none;font-size:12px;font-weight:950;display:inline-flex;align-items:center;justify-content:center;gap:7px;cursor:pointer}.lcp-section-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:12px}.lcp-section-head h2{margin:3px 0 0;font-size:24px;letter-spacing:-.05em;font-weight:950}.lcp-empty{border:1px dashed #cbd5e1;border-radius:20px;padding:18px;color:#64748b;font-weight:800}.lcp-program{border:1px solid #e2e8f0;background:#fff;border-radius:24px;padding:14px;margin-top:11px}.lcp-program.has-reward{background:linear-gradient(180deg,#f0fdfa,#fff);border-color:#99f6e4}.lcp-program-head{display:flex;justify-content:space-between;gap:12px}.lcp-program-head strong{display:block;font-size:17px;font-weight:950;color:#0f172a}.lcp-program-head span{display:block;margin-top:4px;color:#64748b;font-size:13px;font-weight:800}.lcp-program-head em{font-style:normal;border-radius:999px;background:rgba(0,103,95,.08);color:#00675f;padding:8px 11px;height:fit-content;font-weight:950}.lcp-progress{height:10px;border-radius:999px;background:#e2e8f0;overflow:hidden;margin-top:13px}.lcp-progress i{display:block;height:100%;border-radius:999px;background:linear-gradient(135deg,#00675f,#14b8a6)}.lcp-program-meta{display:flex;justify-content:space-between;gap:12px;margin-top:10px}.lcp-program-meta span{font-weight:950}.lcp-program-meta small{color:#64748b;font-weight:850}.lcp-actions-grid{display:grid;grid-template-columns:1fr 1fr 1.2fr;gap:8px;margin-top:13px}.lcp-actions-grid button{min-height:42px;border-radius:15px;border:1px solid #dbe4ef;background:#fff;color:#334155;font-weight:950;cursor:pointer}.lcp-actions-grid button.primary{background:#00675f;border-color:#00675f;color:#fff}.lcp-actions-grid button:disabled{opacity:.5;cursor:not-allowed}@media(max-width:860px){.lcp-hero{display:grid}.lcp-grid{grid-template-columns:1fr}.lcp-customer-card{position:relative;top:auto}.lcp-actions-grid{grid-template-columns:1fr}.lcp-hero h1{font-size:34px}}
.lcp-redemption-card{display:grid;gap:14px}
.lcp-redemption-preview{
  border:1px solid rgba(0,103,95,.16);
  background:linear-gradient(135deg,rgba(0,103,95,.08),#fff);
  border-radius:24px;
  padding:18px;
  text-align:center;
  display:grid;
  justify-items:center;
  gap:8px;
}
.lcp-redemption-preview.is-used{
  border-color:rgba(22,163,74,.22);
  background:linear-gradient(135deg,rgba(22,163,74,.10),#fff);
}
.lcp-redemption-icon{
  width:58px;
  height:58px;
  border-radius:20px;
  display:grid;
  place-items:center;
  background:#e7f7f2;
  color:#00675f;
}
.lcp-redemption-preview strong{
  color:#0f172a;
  font-size:22px;
  line-height:1.05;
  letter-spacing:-.035em;
}
.lcp-redemption-preview p{
  margin:0;
  color:#64748b;
  font-weight:750;
  line-height:1.45;
}
.lcp-confirm-redemption{
  min-height:52px;
  border:0;
  border-radius:18px;
  background:#00675f;
  color:#fff;
  font-size:15px;
  font-weight:950;
  cursor:pointer;
  box-shadow:0 16px 34px rgba(0,103,95,.22);
}
.lcp-confirm-redemption:disabled{
  opacity:.62;
  cursor:not-allowed;
  box-shadow:none;
}


.lcp-result-card{
  border:1px solid rgba(22,163,74,.20);
  background:#f0fdf4;
  color:#166534;
  border-radius:20px;
  padding:13px;
  display:flex;
  align-items:center;
  gap:10px;
}
.lcp-result-card strong{display:block;font-size:14px;font-weight:950;}
.lcp-result-card small{display:block;margin-top:3px;font-size:12px;font-weight:800;color:#166534;opacity:.82;}

.lcp-qr-note{margin-top:14px;color:#64748b;font-size:12px;font-weight:850;line-height:1.35;}


.lcp-booking-link{
  border:1px solid rgba(0,103,95,.14);
  background:#fbfffd;
  border-radius:24px;
  padding:14px;
  display:grid;
  gap:10px;
}
.lcp-booking-link-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:12px;
}
.lcp-booking-link-head span{
  display:block;
  color:#00675f;
  font-size:10px;
  font-weight:950;
  letter-spacing:.14em;
  text-transform:uppercase;
}
.lcp-booking-link-head h3{
  margin:3px 0 0;
  color:#0f172a;
  font-size:18px;
  line-height:1.1;
  letter-spacing:-.035em;
  font-weight:950;
}
.lcp-booking-options{
  display:grid;
  gap:9px;
}
.lcp-booking-option{
  width:100%;
  min-height:58px;
  border:1px solid #dbe4ef;
  background:#fff;
  border-radius:18px;
  padding:11px 12px;
  color:#0f172a;
  display:grid;
  grid-template-columns:auto 1fr auto;
  align-items:center;
  gap:10px;
  text-align:left;
  cursor:pointer;
}
.lcp-booking-general{
  grid-template-columns:1fr auto;
}
.lcp-booking-option.is-selected{
  border-color:#00675f;
  background:linear-gradient(135deg,rgba(0,103,95,.08),#fff);
  box-shadow:0 12px 28px rgba(0,103,95,.10);
}
.lcp-booking-option.is-disabled{
  opacity:.56;
  cursor:not-allowed;
  background:#f8fafc;
}
.lcp-booking-option-icon{
  width:38px;
  height:38px;
  border-radius:14px;
  display:grid;
  place-items:center;
  background:#e7f7f2;
  color:#00675f;
}
.lcp-booking-option strong{
  display:block;
  font-size:14px;
  font-weight:950;
}
.lcp-booking-option small{
  display:block;
  margin-top:3px;
  color:#64748b;
  font-size:12px;
  font-weight:800;
}
.lcp-booking-option em{
  display:block;
  margin-top:4px;
  font-style:normal;
  color:#c2410c;
  font-size:11px;
  font-weight:900;
}
.lcp-booking-empty{
  margin:0;
  border:1px dashed #cbd5e1;
  background:#fff;
  border-radius:16px;
  padding:12px;
  color:#64748b;
  font-size:12px;
  line-height:1.35;
  font-weight:850;
}
.lcp-booking-linked-done{
  border:1px solid rgba(22,163,74,.20);
  background:#f0fdf4;
  color:#166534;
  border-radius:18px;
  padding:12px;
  display:flex;
  align-items:center;
  gap:8px;
  font-size:13px;
  font-weight:950;
}

`;
